/**
 * AsyncContent — the list-fetch wrapper: loading, error, empty, data.
 *
 * Encapsulates the loading / empty / data pattern with FB/LinkedIn-style
 * polish: skeleton is shown only after `showAfter` ms (so fast loads skip
 * it), held for at least `minVisible` ms once shown, then cross-faded
 * (250ms) to the empty state or real content via the .fade-in utility.
 *
 * Usage replaces inline three-state guards on every list page:
 *
 *   <AsyncContent
 *     loading={loading}
 *     isEmpty={items.length === 0}
 *     skeleton={<Skeleton.List shape="tournament-card" count={6} />}
 *     empty={<EmptyState icon={...} title={t.x.noY} hint={t.common.empty.yHint} />}
 *   >
 *     <ActualList items={items} />
 *   </AsyncContent>
 *
 * Why a component instead of leaving the if/else inline: the timing-and-fade
 * polish has to land identically on every page — a forgotten useDeferredLoading
 * call is invisible bug surface. The component makes the pattern un-skippable.
 */
import type { ReactNode } from 'react';
import { useDeferredLoading } from '../../hooks/useDeferredLoading';
import ErrorState from './ErrorState';

interface AsyncContentProps {
  loading: boolean;
  isEmpty?: boolean;
  skeleton: ReactNode;
  empty?: ReactNode;
  children: ReactNode;
  /** Override deferred-loading timings. Defaults: 200ms / 400ms. */
  showAfter?: number;
  minVisible?: number;
  /** #457 — the load failed. Shows ErrorState, never the empty state: an
   *  empty list after a 500 told the user there was nothing to see. */
  error?: boolean;
  /** Load again. Without it the error state has no button. */
  onRetry?: () => void;
  /** Tighter padding of the error state, for a sub-panel of a page (#665). */
  compact?: boolean;
}

export default function AsyncContent({
  loading,
  isEmpty = false,
  skeleton,
  empty,
  children,
  showAfter,
  minVisible,
  error = false,
  onRetry,
  compact = false,
}: AsyncContentProps) {
  const showSkeleton = useDeferredLoading(loading, { showAfter, minVisible });

  // Five phases (`error` since #457). Two invariants:
  //   1. `pending` covers the showAfter window (default 200ms): request is
  //      in flight but the skeleton hasn't appeared yet. Rendering nothing
  //      here prevents the EmptyState from flashing on fast loads (original
  //      empty-state-flash bug, PR #94).
  //   2. `showSkeleton` overrides `loading` once it goes true, so the
  //      minVisible hold from useDeferredLoading is actually enforced.
  //      Without this, the skeleton hard-cuts to data the instant `loading`
  //      flips false — even mid-hold — and 200-400ms responses (most of
  //      our endpoints) produce a ~30ms skeleton blip instead of a held
  //      and faded transition. Fixed 2026-05-15.
  let phase: 'loading' | 'pending' | 'error' | 'empty' | 'data';
  if (showSkeleton) phase = 'loading';
  else if (loading) phase = 'pending';
  else if (error) phase = 'error';
  else if (isEmpty) phase = 'empty';
  else phase = 'data';

  if (phase === 'pending') return null;

  // `key={phase}` forces React to unmount + remount on phase change, which
  // re-runs the .fade-in animation. Without it, the same wrapper persists
  // and CSS-only fade-in only fires once on initial mount.
  return (
    <div key={phase} className="fade-in">
      {phase === 'loading' && skeleton}
      {phase === 'error' && <ErrorState onRetry={onRetry} compact={compact} />}
      {phase === 'empty' && empty}
      {phase === 'data' && children}
    </div>
  );
}
